import React from 'react';
import { Tag } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { supabase } from '../../lib/supabase';

interface TagBudget {
  name: string;
  value: number;
  color: string;
}

interface TagBudgetChartProps {
  formatBudget: (value: number) => string;
}

const CHART_COLORS = [
  '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
  '#EC4899', '#06B6D4', '#F97316', '#6366F1',
];

export function TagBudgetChart({ formatBudget }: TagBudgetChartProps) {
  const [data, setData] = React.useState<TagBudget[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    loadTagBudgets();
  }, []);

  const loadTagBudgets = async () => {
    try {
      const { data: projectsData, error } = await supabase
        .from('projects')
        .select(`
          budget,
          tags:project_tags (
            tag:tags (
              id,
              name
            )
          )
        `)
        .not('budget', 'is', null);

      if (error) throw error;

      const tagTotals = new Map<string, number>();

      projectsData.forEach((project: any) => {
        if (!project.budget || !project.tags) return;

        const budget = parseFloat(project.budget.replace(/[^0-9.-]+/g, ''));
        if (isNaN(budget)) return;

        project.tags.forEach((tagData: any) => {
          const tag = tagData.tag;
          if (!tag) return;

          const currentTotal = tagTotals.get(tag.name) || 0;
          tagTotals.set(tag.name, currentTotal + budget);
        });
      });

      const processedData = Array.from(tagTotals.entries())
        .map(([name, value], index) => ({
          name,
          value,
          color: CHART_COLORS[index % CHART_COLORS.length]
        }))
        .sort((a, b) => b.value - a.value);

      setData(processedData);
    } catch (err) {
      console.error('Error loading tag budgets:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="h-80 flex items-center justify-center">
          <div className="text-gray-500">Loading tag data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="h-80 flex items-center justify-center">
          <div className="text-red-500">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
      <div className="flex items-center mb-4">
        <Tag className="w-5 h-5 text-indigo-600 mr-2" />
        <h2 className="text-xl font-semibold text-gray-800">Budget by Tag</h2>
      </div>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={120}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: any) => formatBudget(value)}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}